<template>
    <div class="w-full max-w-md p-8 mx-auto flex flex-col justify-center min-h-screen">
        <h1 class="font-[700] text-3xl text-center text-[#374151] dark:text-white mb-[1vh]">{{
            t('pages.forgotPassword.step1.title') }}</h1>
        <p class="font-[600] text-base text-center align-middle text-gray-400 mb-[2vh]">
            {{ t('pages.forgotPassword.step1.description') }}
        </p>
        <UForm :state="formState" :schema="schema" @submit="onSubmit" class="space-y-4">
            <UFormField :name="t('pages.forgotPassword.step1.email')" :label="t('pages.forgotPassword.step1.email')">
                <UInput class="w-full" size="xl" v-model="formState.email" type="email"
                    :placeholder="t('pages.forgotPassword.step1.emailPlaceholder')"
                    :disabled="isLoading || isLoginWithSpecifiedEmailMode" :ui="{ base: 'py-3 px-4' }" />
            </UFormField>
            <p v-if="errorMessages" class="text-red-500 text-sm">{{ errorMessages }}</p>
            <UButton type="submit" color="primary" size="xl" class="py-3" block :loading="isLoading">
                {{ t('pages.forgotPassword.step1.continue') }}
            </UButton>
            <UButton type="button" color="neutral" variant="outline" size="xl" class="py-3 dark:bg-gray-700" block
                @click="goBack">
                {{ t('pages.forgotPassword.step1.back') }}
            </UButton>
        </UForm>
    </div>
</template>

<script setup lang="ts">
import type { FormSubmitEvent } from '@nuxt/ui';
import * as yup from 'yup';
import { checkEmailRegisteredApi, sendRegisterCodeApi } from '~/api/auth';
import { useAuth } from '~/composables/useAuth';
import { useToastStore } from '~/stores/toast';
import { useLocalePath } from '#i18n';

const { t } = useI18n();
const router = useRouter();
const localePath = useLocalePath();
const toastStore = useToastStore();
const isLoading = ref(false);
const errorMessages = ref('');
const isLoginWithSpecifiedEmailMode = ref(false);
const { getRedirectUrl } = useAuth();

const emit = defineEmits(['toStep2']);

const formState = ref({
  email: '',
});

const schema = yup.object({
  email: yup
    .string()
    .required(t('pages.forgotPassword.step1.validation.email.required'))
    .email(t('pages.forgotPassword.step1.validation.email.invalid')),
});

async function onSubmit(event: FormSubmitEvent<typeof formState.value>) {
  if (isLoading.value) return;
  try {
    isLoading.value = true;
    const isRegistered = await checkEmailRegisteredApi(event.data.email);
    if (!isRegistered) {
      errorMessages.value = t('pages.forgotPassword.step1.emailNotRegistered');
      return;
    }
    sendRegisterCodeApi(event.data.email);
    emit('toStep2', event.data.email);
  } catch (error) {
    toastStore.error(t('pages.forgotPassword.step1.sendFailed'));
  } finally {
    isLoading.value = false;
  }
}

function goBack() {
  router.push(localePath('/login'));
}

onMounted(() => {
  const redirectDataObj = getRedirectUrl();
  const email = redirectDataObj?.query.email;
  if (email) {
    formState.value.email = email;
    isLoginWithSpecifiedEmailMode.value = true;
  }
});
</script>